import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_opscounsel-ai/artifacts/a8v8akpl_F86DF7B3-E743-4829-B779-387ACA6F68DC_L0_001%20-%20Edited.png";

const PrivacyPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <img 
            src={LOGO_URL} 
            alt="OpsCounsel™ AI" 
            className="h-8 w-auto object-contain"
          />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: December 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section className="bg-primary/5 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Our Privacy Commitment</h2>
            <p className="text-muted-foreground leading-relaxed">
              OpsCounsel™ AI is designed with privacy in mind. We do not collect, process, or store 
              Protected Health Information (PHI), patient data, or sensitive personal information. 
              Our service is for general operational guidance only.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">1. Information We Collect</h2>
            <h3 className="text-lg font-medium text-foreground mt-4 mb-2">Account Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Email address (for account creation and communication)</li>
              <li>Password (encrypted and securely stored)</li>
              <li>Subscription and billing information (processed by Stripe)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-foreground mt-4 mb-2">Usage Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Chat conversation content (operational questions and AI responses)</li>
              <li>Usage patterns (message counts, session duration)</li>
              <li>Device and browser information (for service optimization)</li>
            </ul>
          </section>

          <section className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-amber-800 dark:text-amber-200 mb-4">2. Information We DO NOT Collect</h2>
            <p className="text-amber-700 dark:text-amber-300 mb-4">
              OpsCounsel™ AI is explicitly designed to NOT process the following types of information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-amber-700 dark:text-amber-300">
              <li><strong>Protected Health Information (PHI)</strong> as defined by HIPAA</li>
              <li><strong>Patient names, medical record numbers, or health data</strong></li>
              <li><strong>Social Security numbers or government IDs</strong></li>
              <li><strong>Confidential employee HR records</strong></li>
              <li><strong>Any personally identifiable health information</strong></li>
            </ul>
            <p className="text-amber-700 dark:text-amber-300 mt-4 font-semibold">
              Users are prohibited from entering such information into the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">3. How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>To provide and improve the OpsCounsel™ AI service</li>
              <li>To process subscriptions and payments</li>
              <li>To communicate with you about your account and service updates</li>
              <li>To analyze usage patterns and improve our AI responses</li>
              <li>To ensure service security and prevent abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">4. Data Storage and Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>All data is encrypted in transit using TLS/SSL</li>
              <li>Passwords are hashed using secure algorithms</li>
              <li>Payment information is handled by Stripe (PCI-DSS compliant)</li>
              <li>Regular security audits and monitoring</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">5. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the following third-party services:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>OpenAI:</strong> Powers our AI chat functionality (no PHI is sent)</li>
              <li><strong>Stripe:</strong> Processes payments securely</li>
              <li><strong>Resend:</strong> Delivers transactional emails</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              These services have their own privacy policies and security standards.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account information and conversation history as long as your account 
              is active. You may request deletion of your data at any time by contacting us. 
              Upon account deletion, your personal data will be removed within 30 days, except 
              where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">7. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and data</li>
              <li>Export your conversation history</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">8. HIPAA and BAA Notice</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong>OpsCounsel™ AI is not a HIPAA-covered entity and does not sign Business Associate Agreements (BAAs).</strong>{" "}
              Our service is designed for general operational guidance and is not intended to handle, process, 
              or store Protected Health Information. Healthcare organizations using this service should ensure 
              their staff understands these limitations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              OpsCounsel™ AI is not intended for use by individuals under 18 years of age. 
              We do not knowingly collect information from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">10. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any 
              material changes via email or through the Service. Your continued use of the 
              Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">11. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
              <br />
              <a href="mailto:privacy@opscounsel.ai" className="text-primary hover:underline">
                privacy@opscounsel.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-6 px-4">
        <div className="max-w-4xl mx-auto text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} OpsCounsel™ AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
